import { QdzElement, define, debounce, motion } from '@qdz/element';

export class QdzCarousel extends QdzElement {
  get track() {
    return this.ref('track');
  }

  get slides() {
    return [...(this.track?.children ?? [])];
  }

  mount() {
    if (!this.track) return;
    const previous = this.ref('previous');
    const next = this.ref('next');
    if (previous) this.listen(previous, 'click', () => this.go(-1));
    if (next) this.listen(next, 'click', () => this.go(1));
    this.listen(this.track, 'scroll', debounce(() => this.#update(), 80));
    this.listen(window, 'resize', debounce(() => this.#update(), 150));
    this.#update();
  }

  get index() {
    const start = this.track.scrollLeft;
    const found = this.slides.findIndex((slide) => slide.offsetLeft >= start - 1);
    return Math.max(found, 0);
  }

  go(step) {
    this.show(this.index + step);
  }

  show(index) {
    const slides = this.slides;
    const last = this.#lastStart();
    const position = this.hasAttribute('bounded') ? Math.min(Math.max(index, 0), last) : (index + slides.length) % slides.length;
    const target = slides[position];
    if (!target) return;
    this.track.scrollTo({
      left: target.offsetLeft,
      behavior: motion.scroll(),
    });
  }

  editorSelect(event) {
    const slide = this.slides.find((element) => element.contains(event.target));
    if (slide) this.show(this.slides.indexOf(slide));
  }

  #lastStart() {
    const slides = this.slides;
    const limit = this.track.scrollWidth - this.track.clientWidth;
    const index = slides.findIndex((slide) => slide.offsetLeft >= limit - 1);
    return index < 0 ? slides.length - 1 : index;
  }

  #update() {
    const start = this.track.scrollLeft;
    const end = start + this.track.clientWidth;
    this.toggleAttribute('data-static', this.track.scrollWidth <= this.track.clientWidth + 1);
    if (this.hasAttribute('bounded')) {
      const previous = this.ref('previous');
      const next = this.ref('next');
      if (previous) previous.disabled = start <= 1;
      if (next) next.disabled = end >= this.track.scrollWidth - 1;
    }
    for (const slide of this.slides) {
      const left = slide.offsetLeft;
      const visible = left >= start - 1 && left + slide.offsetWidth <= end + 1;
      slide.toggleAttribute('inert', !visible);
    }
    this.emit('carousel:change', { index: this.index });
  }
}

define('qdz-carousel', QdzCarousel);
